// Considered: ScopeCoinAtom — rejected; it is a marketplace coin, not a generic picker.
// IconPickerAtom is the Iconify-backed picker; ScopeCoinAtom delegates to GlyphAtom for rendering.
// See spec Reuse Audit.

import React, { useState, useRef, useMemo } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Icon, addCollection } from '@iconify/react';
import lucideData from '@iconify-json/lucide/icons.json';
import { useTranslation } from 'localization';
import './IconPickerAtom.scss';

// Register Lucide offline — mirrors GlyphAtom bootstrap; idempotent
addCollection(lucideData as never);

// Map of sets that are bundled at build time (no CDN).
// Add new sets here when @iconify-json/<set> is installed.
const BUNDLED_SETS: Record<string, { prefix: string; icons: Record<string, unknown> }> = {
  lucide: lucideData as { prefix: string; icons: Record<string, unknown> },
};

function getIconsForSets(sets: string[]): string[] {
  return sets.flatMap((set) => {
    const data = BUNDLED_SETS[set];
    if (!data) return [];
    return Object.keys(data.icons).map((name) => `${data.prefix}:${name}`);
  });
}

// Determines how to display the current value in the trigger button.
// The picker only emits/stores Iconify `set:name` values; an empty or
// unrecognised value falls back to a neutral placeholder icon.
function renderValueIcon(value: string): React.ReactNode {
  const icon = value.includes(':') ? value : 'lucide:circle';
  return <Icon icon={icon} className="icon-picker-trigger-glyph" />;
}

export interface IconPickerAtomProps {
  /** Currently selected icon — Iconify `set:name` (e.g. 'lucide:building') */
  value?: string;
  /** Callback when an icon is selected; receives Iconify `set:name` for new selections */
  onChange: (icon: string) => void;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Tooltip / a11y label for the trigger button */
  tooltip?: string;
  /** Bundled sets to offer; must be keys of BUNDLED_SETS. Defaults to ['lucide']. */
  sets?: string[];
  /** Show the optional color-control panel (token swatches + hue slider) */
  showColorControl?: boolean;
  /** Current color value for the color control */
  glyphColor?: { kind: 'token'; value: string } | { kind: 'hue'; value: number };
  /** Callback when user changes the color */
  onColorChange?: (color: { kind: 'token'; value: string } | { kind: 'hue'; value: number }) => void;
}

// Design-token slugs offered as color swatches
const TOKEN_SWATCHES = [
  'scope-organization',
  'scope-department',
  'scope-group',
  'scope-team',
  'scope-project',
  'neutral',
  'primary',
] as const;

const IconPickerAtom: React.FC<IconPickerAtomProps> = ({
  value = 'lucide:circle',
  onChange,
  disabled = false,
  className = '',
  tooltip,
  sets = ['lucide'],
  showColorControl = false,
  glyphColor,
  onColorChange,
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<OverlayPanel>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Build the full icon list from configured sets (memoised — set list rarely changes)
  const allIcons = useMemo(() => getIconsForSets(sets), [sets]);

  const filteredIcons = useMemo(() => {
    if (!searchTerm) return allIcons;
    const term = searchTerm.toLowerCase();
    return allIcons.filter((icon) => {
      const name = icon.includes(':') ? icon.split(':')[1] : icon;
      return name?.includes(term) ?? false;
    });
  }, [allIcons, searchTerm]);

  const handleIconSelect = (icon: string) => {
    onChange(icon);
    overlayRef.current?.hide();
    setSearchTerm('');
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    if (!disabled) {
      overlayRef.current?.toggle(e);
    }
  };

  const triggerLabel = tooltip || t('iconPicker.selectIcon');

  return (
    <div className={`icon-picker-atom ${className}`}>
      <Button
        type="button"
        className="icon-picker-trigger"
        onClick={handleButtonClick}
        disabled={disabled}
        tooltip={triggerLabel}
        tooltipOptions={{ position: 'top' }}
        aria-label={triggerLabel}
      >
        {renderValueIcon(value)}
        <i className="pi pi-chevron-down icon-picker-chevron" />
      </Button>

      <OverlayPanel
        ref={overlayRef}
        className="icon-picker-overlay"
        dismissable
        onHide={() => setSearchTerm('')}
      >
        <div className="icon-picker-content">
          <div className="icon-picker-search">
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('iconPicker.searchPlaceholder')}
                className="icon-picker-search-input"
              />
            </span>
          </div>

          <div className="icon-picker-grid">
            {filteredIcons.map((icon) => {
              const iconName = icon.includes(':') ? icon.split(':')[1] : icon;
              return (
                <button
                  key={icon}
                  type="button"
                  className={`icon-picker-item ${value === icon ? 'selected' : ''}`}
                  onClick={() => handleIconSelect(icon)}
                  title={iconName}
                  data-glyph={icon}
                >
                  <Icon icon={icon} />
                </button>
              );
            })}
            {filteredIcons.length === 0 && (
              <div className="icon-picker-empty">
                {t('iconPicker.noResults')}
              </div>
            )}
          </div>

          {showColorControl && onColorChange && (
            <div className="icon-picker-color-control">
              <div className="icon-picker-color-label">{t('iconPicker.colorLabel')}</div>
              <div className="icon-picker-token-swatches">
                {TOKEN_SWATCHES.map((token) => (
                  <button
                    key={token}
                    type="button"
                    className={`icon-picker-token-swatch ${glyphColor?.kind === 'token' && glyphColor.value === token ? 'selected' : ''}`}
                    onClick={() => onColorChange({ kind: 'token', value: token })}
                    title={token}
                    style={{ '--swatch-color': `var(--${token})` } as React.CSSProperties}
                    aria-label={t('iconPicker.tokenSwatchAriaLabel', { token })}
                  />
                ))}
              </div>
              <div className="icon-picker-hue-row">
                <label className="icon-picker-hue-label" htmlFor="icon-picker-hue">
                  {t('iconPicker.hueLabel')}
                </label>
                <input
                  id="icon-picker-hue"
                  type="range"
                  min={0}
                  max={359}
                  value={glyphColor?.kind === 'hue' ? glyphColor.value : 180}
                  onChange={(e) => onColorChange({ kind: 'hue', value: Number(e.target.value) })}
                  className="icon-picker-hue-slider"
                  aria-label={t('iconPicker.hueLabel')}
                />
              </div>
            </div>
          )}
        </div>
      </OverlayPanel>
    </div>
  );
};

export default IconPickerAtom;
